import { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { queryClient } from '@/App';
import EmptyIcon from '@/assets/icons/empty.svg';
import { RedmineStatus } from '@/constants/TrainingPlanning';
import { columns } from '@/constants/tableColumns/TrainingFileTeamsColumns';
import { trainingFileStatus } from '@/constants/trainingFileStatus';
import api from '@/services/apiSgft';
import { TrainingFile, TrainingFileTeam } from '@/types/TrainingFile';
import { cn } from '@/utils/cn';
import { dateInUTCFormat } from '@/utils/formatDate';
import { getFirstAndLastName } from '@/utils/getFirstAndLastName';

import LightTooltip from '../atoms/LightTooltip';
import SimpleMenu from '../atoms/Menu';
import ModalConfirmation from '../molecules/ConfirmationModal';
import { TrainingFileInstructors } from '../molecules/TrainingFileInstructors';
import TrainingFileModal from '../molecules/TrainingFileModal';
import Table from './Table';

interface TrainingFileCardProps {
	trainingFile: TrainingFile;
}
const TrainingFileCard = ({ trainingFile }: TrainingFileCardProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [disableSendToApproval, setDisableSendToApproval] = useState(false);
	const [disableSendEmployeeApproval, setDisableSendEmployeeApproval] =
		useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isInstructorsModalOpen, setIsInstructorsModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const navigate = useNavigate();
	const handleToggleCard = () => {
		setIsOpen((prev) => !prev);
	};
	const handleOpenDeleteModal = () => {
		setIsDeleteModalOpen(true);
	};
	const handleCloseDeleteModal = () => {
		setIsDeleteModalOpen(false);
	};
	const handleOpenEditModal = () => {
		setIsEditModalOpen(true);
	};
	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
	};

	const handleOpenInstructorsModal = () => {
		setIsInstructorsModalOpen(true);
	};
	const handleCloseInstructorsModal = () => {
		setIsInstructorsModalOpen(false);
	};
	const handleDeleteModal = async () => {
		try {
			await api.delete(`/training-files/${trainingFile.id}`);
			toast.success(
				'Ficheiro excluído internamente com sucesso! Em instantes, as mudanças serão refletidas no BPMS.',
				{ theme: 'colored' },
			);
			queryClient.invalidateQueries(['training-files']);
		} catch (error: any) {
			const status = error?.response?.status;
			const msg = error?.response?.data?.message || 'Erro ao excluir ficheiro';

			if (status === 404 || status === 409) {
				toast.warn(msg, {
					theme: 'colored',
					toastId: 'warning',
				});
				setTimeout(() => {
					window.location.reload();
				}, 3000);
			} else {
				toast.error(`Erro: ${msg}`, {
					theme: 'colored',
					toastId: 'error',
				});
			}
		}
	};
	const handleOpenNewTrainingPlanning = () => {
		navigate(`/home/ficheiros/${trainingFile.id}/planejamento-de-turma`);
	};

	const handleExtractTrainingFile = async () => {
		try {
			const response = await api.get(`/extraction/training-file`, {
				params: {
					trainingFileId: trainingFile.id,
				},
				responseType: 'blob',
			});
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				}),
			);
			const link = document.createElement('a');

			link.href = url;
			link.setAttribute('download', 'Extração Ficheiro.xlsx');
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (e) {
			toast.error('Erro ao realizar extração', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const handleSendToApproval = async () => {
		try {
			setDisableSendToApproval(true);
			const response = await api.post(
				`/training-files/${trainingFile.id}/send-to-approval`,
			);

			if (response.data?.passedWithErrors) {
				toast.warn(response.data.message, {
					theme: 'colored',
					toastId: 'warning',
				});
				return;
			}

			toast.success(
				'Ficheiro enviado para aprovação dos gestores. Em instantes, as mudanças serão refletidas no sistema BPMS.',
				{
					theme: 'colored',
					toastId: 'success',
				},
			);
		} catch (e) {
			console.log(e);
			toast.error('Erro ao enviar para aprovação', {
				theme: 'colored',
				toastId: 'error',
			});
			setDisableSendToApproval(false);
		}
	};

	const handleSendEmployeeApproval = async () => {
		let teamId;
		let passedWithError = false;
		try {
			setDisableSendEmployeeApproval(true);
			for (const team of trainingFile.teams) {
				if (teamIsValidForSendEmployeeApproval(team)) {
					teamId = team.id;
					const response = await api.post(
						`/training-plannings/${teamId}/send-to-employee-approval`,
					);
					if (response.data?.passedWithError) {
						passedWithError = true;
					}
				}
			}

			toast.success(
				'Convocações enviadas. Em instantes, serão refletidas no sistema BPMS. ',
				{
					theme: 'colored',
					toastId: 'success',
				},
			);
		} catch (e) {
			toast.error(`Erro ao enviar convocações para a turma ${teamId}`, {
				theme: 'colored',
				toastId: 'error',
			});
			setDisableSendEmployeeApproval(false);
		}
	};

	const shouldDisableSendEmployeeApproval = () => {
		if (
			!trainingFile.sentToApproval ||
			trainingFileClosed ||
			trainingFile.teams.length === 0 ||
			disableSendEmployeeApproval
		) {
			return true;
		}
		for (const team of trainingFile.teams) {
			if (teamIsValidForSendEmployeeApproval(team)) {
				return false;
			}
		}
		return true;
	};

	const teamIsValidForSendEmployeeApproval = (team: TrainingFileTeam) => {
		const hasSomeApproved = team.employees?.some(
			(emp) =>
				emp.redmineManagerStatus?.toLocaleUpperCase() ===
				RedmineStatus.SolicitacaoAprovada,
		);
		if (!team.sentToApproval && hasSomeApproved) {
			return true;
		}
		return false;
	};

	const trainingFileClosed =
		trainingFile.status === trainingFileStatus.FINISHED ||
		trainingFile.status === trainingFileStatus.CONCLUDED;

	const options = [
		{
			name: 'Adicionar Novo Planejamento',
			onClick: handleOpenNewTrainingPlanning,
			disabled: trainingFileClosed,
		},
		{
			name: 'Detalhamento das Turmas',
			onClick: () =>
				navigate(`/home/ficheiros/detalhamento/${trainingFile.id}`),
			disabled: trainingFile.teams.length === 0,
		},
		{
			name: 'Cadastrar Instrutores',
			onClick: handleOpenInstructorsModal,
			disabled: trainingFileClosed,
		},
		{
			name: 'Extrair Ficheiro',
			onClick: handleExtractTrainingFile,
			disabled: trainingFile.teams.length === 0,
		},
		{
			name: 'Editar Ficheiro',
			onClick: handleOpenEditModal,
			disabled: trainingFileClosed,
		},
		{
			name: 'Excluir Ficheiro',
			onClick: handleOpenDeleteModal,
			disabled: trainingFileClosed,
		},
		{
			name: 'Enviar para Aprovação',
			onClick: handleSendToApproval,
			disabled:
				trainingFile.sentToApproval ||
				trainingFileClosed ||
				trainingFile.teams.length === 0 ||
				disableSendToApproval,
		},
		{
			name: 'Enviar Convocações',
			onClick: handleSendEmployeeApproval,
			disabled: shouldDisableSendEmployeeApproval(),
		},
	];
	return (
		<div className="flex min-w-full flex-col rounded-md bg-white">
			{isDeleteModalOpen && (
				<ModalConfirmation
					confirmAction={handleDeleteModal}
					title="Excluir Ficheiro"
					isOpen={isDeleteModalOpen}
					onClose={handleCloseDeleteModal}
					description={`Todas as turmas planejadas serão canceladas. Tem certeza que deseja excluir o ficheiro ${trainingFile.name} (${trainingFile.trainingFileNumber})?`}
				/>
			)}
			{isEditModalOpen && (
				<TrainingFileModal
					isOpen={isEditModalOpen}
					trainingFile={trainingFile}
					onClose={handleCloseEditModal}
				/>
			)}
			{isInstructorsModalOpen && (
				<TrainingFileInstructors
					onClose={handleCloseInstructorsModal}
					trainingFile={trainingFile}
				/>
			)}
			<div
				className={cn(
					'flex min-h-16 w-full justify-between gap-2 rounded-lg bg-white p-2 text-xs text-gray-600',
					trainingFile.teams.length === 0 && isOpen ? 'rounded-b-none' : '',
				)}
			>
				<button onClick={handleToggleCard} type="button">
					<FaChevronDown
						className={`ml-auto fill-primary transition-all ${
							isOpen && 'rotate-180'
						}`}
					/>
				</button>
				<div className="flex w-full items-center justify-between  gap-2">
					<div className="flex w-72 items-center justify-start gap-2 overflow-hidden whitespace-nowrap py-1">
						<div className="flex  h-8 items-center gap-2 overflow-hidden rounded-lg bg-[#CFDEFF] px-2 py-1 text-primary">
							Ficheiro:
							<LightTooltip title={trainingFile.name} arrow placement="top-end">
								<span className="overflow-hidden py-2 font-bold">
									{trainingFile.name}
								</span>
							</LightTooltip>
						</div>
						<SimpleMenu options={options} upDotsButton />
					</div>

					<div className="flex h-8  items-center gap-2 whitespace-nowrap   px-2 py-1">
						Número:
						<span className="font-bold">{trainingFile.trainingFileNumber}</span>
					</div>
					<div className="flex  w-24 items-center  gap-2 whitespace-nowrap rounded-lg px-2 py-1  ">
						Status:
						<span className="font-bold">{trainingFile.status}</span>
					</div>
					<div className="flex w-72 items-center  gap-2 whitespace-nowrap rounded-lg px-2 py-1  ">
						Período de realização:
						<span className="font-bold">
							{`${new Date(trainingFile.startDate).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} - ${new Date(trainingFile.endDate).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}`}
						</span>
					</div>
					<div className="flex max-w-60 items-center  gap-2 overflow-hidden whitespace-nowrap rounded-lg px-2 py-1 ">
						Fornecedor:
						<LightTooltip
							title={trainingFile.supplier.name}
							arrow
							placement="top-end"
						>
							<span className="overflow-hidden text-ellipsis py-2 font-bold">
								{trainingFile.supplier.name}
							</span>
						</LightTooltip>
					</div>
					<div className="flex  w-52 items-center  gap-2 overflow-hidden whitespace-nowrap rounded-lg px-2  py-1">
						Responsável:
						<LightTooltip
							title={trainingFile.responsible}
							arrow
							placement="top-end"
						>
							<span className="overflow-hidden text-ellipsis py-2 font-bold">
								{getFirstAndLastName(trainingFile.responsible)}
							</span>
						</LightTooltip>
					</div>
				</div>
			</div>
			{isOpen &&
				(trainingFile.teams.length > 0 ? (
					<div className="min-h-24 px-4 py-2">
						<Table
							columns={columns}
							data={trainingFile.teams.map((team) => ({
								...team,
								concluded:
									trainingFile.status === trainingFileStatus.CONCLUDED ||
									trainingFile.status === trainingFileStatus.FINISHED,
								planningDays: team.planningDays.map((planningDay) => ({
									...planningDay,
									date: dateInUTCFormat(planningDay.date).toISOString(),
								})),
							}))}
							// isWhiteTable={true}
							tBodyClassname="border-none "
							trBodyClassname="border-none"
							tdHeaderClassname="bg-transparent"
							trHeaderClassname="bg-transparent my-4 rounded-md"
							tdClassname="px-0 align-top"
							containerClassname="border-separate border-spacing-y-4"
						/>
					</div>
				) : (
					<div className="flex min-h-40 w-full flex-col items-center justify-center rounded-b-md rounded-t-none bg-white pb-2">
						<img src={EmptyIcon} className="w-32" />
						<div className="w-full text-center text-xs">
							<span className="font-semibold text-blue-800">
								Nenhuma turma cadastrada para esse ficheiro!
							</span>
						</div>
					</div>
				))}
		</div>
	);
};
export default TrainingFileCard;
